import {
    type ApiResult,
    type BaseParams,
    type EmojiAddPaymentRequest,
    type EmojiGroupCheckResponse,
    type EmojiId,
    type EmojiPaymentResponse,
    type EmojiSignatureParams,
    type ExtendedEmoji,
    links,
    type PostParams,
    type RandomResponse,
    type RecentEmojiReponse,
    Request,
    type RhythmResponse,
    type SearchQuery,
    type SearchResponse,
    type SignatureResponse,
} from '@app/client';

import { apiFactory, apiReturnResults } from '../../ApiFactory';

export async function getExtendedCollection(params: BaseParams): Promise<ApiResult<ExtendedEmoji[]>> {
    return Request.getRequest<ExtendedEmoji[]>({
        ...params,
        target: links.emoji().extended(),
    });
}

export async function search(params: BaseParams<SearchQuery>): Promise<ApiResult<SearchResponse>> {
    return Request.getRequest<SearchResponse>({
        ...params,
        target: links.emoji().search(),
    });
}

export async function random(params: BaseParams<SearchQuery>): Promise<ApiResult<RandomResponse>> {
    return Request.getRequest<RandomResponse>({
        ...params,
        target: links.emoji().random(),
    });
}

export async function check(params: BaseParams & { eid: EmojiId }): Promise<ApiResult<EmojiGroupCheckResponse>> {
    return Request.getRequest<EmojiGroupCheckResponse>({
        ...params,
        target: links.emoji().groups.check(encodeURIComponent(params.eid)),
    });
}

export async function rhythm(params: { eid: EmojiId }): Promise<ApiResult<RhythmResponse>> {
    return Request.getRequest<RhythmResponse>({
        ...params,
        target: links.emoji().rhythm(params.eid),
    });
}

export async function sign(
    params: BaseParams & PostParams<EmojiSignatureParams> & { eid: EmojiId },
): Promise<ApiResult<SignatureResponse>> {
    return Request.postRequest<SignatureResponse>({
        ...params,
        target: links.artifacts().single('Yat').generateSignature(params.eid),
    });
}

export async function payment(params: BaseParams & { eid: EmojiId }): Promise<ApiResult<EmojiPaymentResponse>> {
    return Request.getRequest<EmojiPaymentResponse>({
        ...params,
        target: links.emoji().payment(params.eid),
    });
}

export async function addPayment(
    params: BaseParams & PostParams<EmojiAddPaymentRequest> & { eid: EmojiId },
): Promise<ApiResult<EmojiId>> {
    return Request.patchRequest<EmojiId>({
        ...params,
        target: links.emoji().tags(params.eid).collection(),
    });
}

export const callEmojiRhythm = async (params: BaseParams & { eid: EmojiId }) => {
    const res = await apiFactory().call(rhythm, {
        ...params,
    });
    if (res.ok) {
        return res.value;
    }
    const errorMessage = res?.error?.detail ?? `Error getting emoji data`;
    throw new Error(errorMessage);
};

async function getRecentEmojis(params: BaseParams): Promise<ApiResult<RecentEmojiReponse>> {
    return Request.getRequest({
        ...params,
        target: links.emoji().recent(),
        settings: {
            ...params.settings,
        },
    });
}

export const callGetRecentEmojis = (params: BaseParams) =>
    apiReturnResults<RecentEmojiReponse>(getRecentEmojis, params);
